// src/utils/couponUtils.js
import {
  collection, query, where, getDocs,
  doc, getDoc, updateDoc, addDoc,
  increment, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase/config';

// ── Validate and apply a coupon code ─────────────────────────────
// Returns: { valid, discount, coupon, error }
export async function validateCoupon(code, cartTotal, userId) {
  if (!code?.trim()) return { valid: false, error: 'Enter a coupon code' };

  try {
    // Fetch coupon by code (case-insensitive — stored uppercase)
    const q    = query(
      collection(db, 'coupons'),
      where('code',     '==', code.trim().toUpperCase()),
      where('isActive', '==', true)
    );
    const snap = await getDocs(q);

    if (snap.empty) return { valid: false, error: 'Invalid coupon code' };

    const coupon = { id: snap.docs[0].id, ...snap.docs[0].data() };

    // ── Check expiry ──
    if (coupon.expiresAt) {
      const expiry = coupon.expiresAt?.toDate?.() || new Date(coupon.expiresAt);
      if (new Date() > expiry) return { valid: false, error: 'This coupon has expired' };
    }

    // ── Check max total uses ──
    if (coupon.maxUses && (coupon.usedCount || 0) >= coupon.maxUses) {
      return { valid: false, error: 'Coupon usage limit reached' };
    }

    // ── Check minimum order ──
    if (coupon.minOrder && cartTotal < coupon.minOrder) {
      return {
        valid: false,
        error: `Minimum order ₹${coupon.minOrder} required (add ₹${coupon.minOrder - cartTotal} more)`,
      };
    }

    // ── Check new-user only ──
    if (coupon.newUserOnly && userId) {
      const ordersQ  = query(collection(db, 'orders'), where('userId', '==', userId));
      const ordersSnap = await getDocs(ordersQ);
      if (!ordersSnap.empty) {
        return { valid: false, error: 'This coupon is for new customers only' };
      }
    }

    // ── Check per-user limit ──
    if (userId && coupon.perUser) {
      const usageQ  = query(
        collection(db, 'couponUsage'),
        where('couponId', '==', coupon.id),
        where('userId',   '==', userId)
      );
      const usageSnap = await getDocs(usageQ);
      const timesUsed = usageSnap.size;
      if (timesUsed >= (coupon.perUser || 1)) {
        return { valid: false, error: `You've already used this coupon ${coupon.perUser > 1 ? `${coupon.perUser} times` : ''}` };
      }
    }

    // ── Calculate discount ──
    let discount = 0;
    if (coupon.type === 'percent') {
      discount = Math.round(cartTotal * coupon.value / 100);
    } else {
      discount = Math.min(coupon.value, cartTotal); // can't discount more than cart
    }

    return { valid: true, discount, coupon };
  } catch (err) {
    console.error('validateCoupon error:', err);
    return { valid: false, error: 'Could not verify coupon. Try again.' };
  }
}

// ── Record coupon usage after successful order ────────────────────
export async function recordCouponUsage(couponId, orderId, userId, discount) {
  try {
    // Increment usedCount on coupon
    await updateDoc(doc(db, 'coupons', couponId), {
      usedCount: increment(1),
    });
    // Record usage for per-user limit checks
    await addDoc(collection(db, 'couponUsage'), {
      couponId,
      orderId,
      userId,
      discount,
      usedAt: serverTimestamp(),
    });
  } catch (err) {
    console.error('recordCouponUsage error:', err);
  }
}

// ── Auto-fetch coupons that should be auto-applied ───────────────
export async function getAutoApplyCoupon(cartTotal, userId) {
  try {
    const q    = query(
      collection(db, 'coupons'),
      where('isActive',   '==', true),
      where('autoApply',  '==', true)
    );
    const snap = await getDocs(q);

    for (const d of snap.docs) {
      const coupon = { id: d.id, ...d.data() };
      const result = await validateCoupon(coupon.code, cartTotal, userId);
      if (result.valid) return result; // return first valid auto coupon
    }
    return null;
  } catch (err) {
    console.error('getAutoApplyCoupon error:', err);
    return null;
  }
}
